import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ECONOMICS_PARAMETERS, ECONOMICS_METRICS } from "@/data/content";

const CARD_BG = ["bg-white", "bg-glacier", "bg-glacier", "bg-white"];

export const Economics = () => (
  <section id="economics" data-testid="economics-section" className="bg-surfaceLight px-6 py-[30px] sm:py-[40px] lg:px-12 lg:py-[70px]">
    <div className="lg:mx-auto lg:max-w-[1280px]">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55 }}
          className="lg:col-span-7"
        >
          <h2 className="max-w-xl font-heading text-[30px] font-semibold leading-tight text-ink sm:text-[35px] lg:text-[41px]">
            Resilience that makes sense
          </h2>
          <p className="mt-4 lg:mt-[36px] max-w-lg lg:max-w-[540px] text-[15px] leading-relaxed text-inkSoft">
            Every project begins with the farm's actual load profile, existing energy costs,
            diesel use, outage exposure and expansion requirements.
          </p>

          <div data-testid="economics-approach-transition" className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
            <div>
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-inkMuted">Current Approach</span>
              <p data-testid="economics-approach-current" className="mt-1 font-heading text-lg font-semibold text-ink">
                Grid + Diesel
              </p>
            </div>
            <ArrowRight size={15} className="text-teal shrink-0" aria-hidden="true" />
            <div className="border-l-2 border-teal pl-4">
              <span className="text-xs font-medium uppercase tracking-[0.1em] text-teal">Hybrid Approach</span>
              <p data-testid="economics-approach-hybrid" className="mt-1 font-heading text-lg font-semibold text-ink">
                Renewables + Battery + Hydrogen
              </p>
            </div>
          </div>

          <div className="mt-[30px]">
            <span className="text-xs font-medium uppercase tracking-[0.1em] text-inkMuted">What We Evaluate</span>
            <p data-testid="economics-metrics" className="mt-3 max-w-md lg:max-w-none text-sm leading-relaxed text-inkSoft">
              {ECONOMICS_METRICS.join(" · ")}
            </p>
          </div>

          <Link
            to="/economics"
            data-testid="economics-cta"
            className="group mt-[26px] lg:mt-[46px] flex w-fit items-center gap-1.5 text-sm font-medium text-teal hover:text-tealHover transition-colors duration-300"
          >
            Explore Project Economics
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="lg:col-span-5"
        >
          <span data-testid="economics-parameters-label" className="text-xs font-semibold uppercase tracking-[0.12em] text-teal">
            Illustrative Farm System
          </span>
          <div data-testid="economics-parameters" className="mt-4 grid grid-cols-2 gap-3">
            {ECONOMICS_PARAMETERS.map((param, index) => (
              <div key={param.label} className={`rounded-2xl p-6 ${CARD_BG[index]}`}>
                <span className="font-heading text-3xl font-semibold leading-none text-ink sm:text-[34px]">{param.value}</span>
                <p className="mt-2.5 text-sm text-inkMuted">{param.label}</p>
              </div>
            ))}
          </div>
          <p data-testid="economics-illustrative-note" className="mt-5 text-xs italic text-inkMuted">
            Illustrative engineering example — not an installed project.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);
